import type { RouteLocationNormalized, RouteLocationRaw, RouteParams } from "vue-router";

import { Schulform } from "@core/asd/types/schule/Schulform";
import { DeveloperNotificationException } from "@core/core/exceptions/DeveloperNotificationException";
import { BenutzerKompetenz } from "@core/core/types/benutzer/BenutzerKompetenz";
import { ServerMode } from "@core/core/types/ServerMode";
import type { TabData } from "@ui/ui/nav/TabData";

import { RouteEinstellungenMenuGroup } from "../RouteEinstellungenMenuGroup";
import type { BenutzerAppProps } from "~/components/einstellungen/benutzer/SBenutzerAppProps";
import type { BenutzerAuswahlProps } from "~/components/einstellungen/benutzer/SBenutzerAuswahlProps";
import { RouteDataEinstellungenBenutzer } from "~/router/apps/einstellungen/benutzer/RouteDataEinstellungenBenutzer";
import { routeEinstellungenBenutzerDaten } from "~/router/apps/einstellungen/benutzer/RouteEinstellungenBenutzerDaten";
import { routeEinstellungen } from "~/router/apps/einstellungen/RouteEinstellungen";
import type { RouteApp } from "~/router/apps/RouteApp";
import { routeError } from "~/router/error/RouteError";
import { RouteManager } from "~/router/RouteManager";
import { RouteNode } from "~/router/RouteNode";

const SBenutzerAuswahl = () => import("~/components/einstellungen/benutzer/SBenutzerAuswahl.vue");
const SBenutzerApp = () => import("~/components/einstellungen/benutzer/SBenutzerApp.vue");

export class RouteEinstellungenBenutzer extends RouteNode<RouteDataEinstellungenBenutzer, RouteApp> {

	public constructor() {
		super(Schulform.values(), [BenutzerKompetenz.ADMIN], "einstellungen.benutzer", String.raw`einstellungen/benutzer/:id(\d+)?`, SBenutzerApp, new RouteDataEinstellungenBenutzer());
		super.mode = ServerMode.STABLE;
		super.propHandler = (route) => this.getProps(route);
		super.text = "Benutzer";
		super.menugroup = RouteEinstellungenMenuGroup.BENUTZERVERWALTUNG;
		super.setView("liste", SBenutzerAuswahl, (route) => this.getAuswahlProps(route));
		super.children = [
			routeEinstellungenBenutzerDaten,
		];
		super.defaultChild = routeEinstellungenBenutzerDaten;
	}

	public async beforeEach(to: RouteNode<any, any>, to_params: RouteParams, from: RouteNode<any, any> | undefined, from_params: RouteParams): Promise<boolean | void | Error | RouteLocationRaw> {
		try {
			const { id } = RouteNode.getIntParams(to_params, ["id"]);
			if (id !== undefined) {
				return routeEinstellungenBenutzer.getRoute({ id });
			}
			return true;
		} catch (e) {
			return await routeError.getErrorRoute(e as DeveloperNotificationException);
		}
	}

	protected async update(to: RouteNode<any, any>, to_params: RouteParams): Promise<void | Error | RouteLocationRaw> {
		try {
			const { id } = RouteNode.getIntParams(to_params, ["id"]);
			await this.data.ladeListe();
			if (to.name === this.name) {
				if (this.data.mapBenutzer.size === 0) {
					return;
				}
				const [next] = this.data.mapBenutzer.values();
				return this.getRouteDefaultChild({ id: next.id });
			}
			const eintrag = (id !== undefined) ? this.data.mapBenutzer.get(id) : undefined;
			// Weiterleitung an das erste Objekt in der Liste, wenn id nicht vorhanden ist.
			if (eintrag === undefined) {
				const [next] = this.data.mapBenutzer.values();
				return routeEinstellungenBenutzerDaten.getRoute({ id: next.id });
			}
			await this.data.setBenutzer(eintrag);
		} catch (e) {
			return await routeError.getErrorRoute(e as DeveloperNotificationException);
		}

	}

	public getAuswahlProps(to: RouteLocationNormalized): BenutzerAuswahlProps {
		return {
			auswahl: () => this.data.auswahl,
			mapBenutzer: this.data.mapBenutzer,
			gotoBenutzer: this.data.gotoBenutzer,
			createBenutzerAllgemein: this.data.createBenutzerAllgemein,
			deleteBenutzerMenge: this.data.deleteBenutzerMenge,
		};
	}

	public getProps(to: RouteLocationNormalized): BenutzerAppProps {
		return {
			auswahl: () => this.data.auswahl,
			tabManager: () => this.createTabManagerByChildren(this.data.view.name, this.setTab),
		};
	}

	private setTab = async (value: TabData) => {
		if (value.name === this.data.view.name) {
			return;
		}
		const node = RouteNode.getNodeByName(value.name);
		if (node === undefined) {
			throw new DeveloperNotificationException("Unbekannte Route");
		}
		await RouteManager.doRoute(node.getRoute());
		this.data.setView(node, routeEinstellungen.children);
	};

}

export const routeEinstellungenBenutzer = new RouteEinstellungenBenutzer();
